// ============================================================
// FILE LOCATION: src/components/TimelineBuilder/utils.ts
// ============================================================

/**
 * Timeline Builder Utilities
 * Date parsing, normalization, sorting, and validation
 */

import { TimelineEvent, DatePrecision, RelativeDate } from "./types";

/**
 * Generate a unique ID for new events
 */
export function generateEventId(): string {
  return `evt_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Parse a date string and return precision info
 */
export function parseDateInput(input: string): {
  precision: DatePrecision;
  startDate?: string;
  endDate?: string;
  dateLabel: string;
  relativeValue?: RelativeDate;
} {
  const trimmed = input.trim().toLowerCase();

  // Check for relative dates like "~6 weeks ago" or "6 weeks ago"
  const relativeMatch = trimmed.match(
    /^~?(\d+)\s*(day|week|month|year)s?\s*(ago)?$/i
  );
  if (relativeMatch) {
    const value = parseInt(relativeMatch[1], 10);
    const unit = (relativeMatch[2].toLowerCase() + "s") as RelativeDate["unit"];
    const computed = computeRelativeDate(value, unit, "ago");
    return {
      precision: "relative",
      startDate: computed.toISOString().split("T")[0],
      dateLabel: `~${value} ${unit} ago`,
      relativeValue: { value, unit, direction: "ago" },
    };
  }

  // Check for duration ranges like "6-8 weeks" or "6–8 weeks"
  const durationMatch = trimmed.match(
    /^(\d+)[-–](\d+)\s*(day|week|month|year)s?$/i
  );
  if (durationMatch) {
    return {
      precision: "duration",
      dateLabel: `${durationMatch[1]}-${durationMatch[2]} ${durationMatch[3]}s`,
    };
  }

  // Check for "X weeks/months" duration
  const simpleDurationMatch = trimmed.match(
    /^(\d+)\s*(day|week|month|year)s?$/i
  );
  if (simpleDurationMatch) {
    return {
      precision: "duration",
      dateLabel: `${simpleDurationMatch[1]} ${simpleDurationMatch[2]}s`,
    };
  }

  // Check for month + year like "Jan 2026" or "January 2026"
  const monthYearMatch = trimmed.match(
    /^(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*\s*(\d{4})$/i
  );
  if (monthYearMatch) {
    const monthNum = getMonthNumber(monthYearMatch[1]);
    const year = monthYearMatch[2];
    const startDate = `${year}-${monthNum.toString().padStart(2, "0")}-01`;
    return {
      precision: "month",
      startDate,
      dateLabel: formatMonthYear(monthNum, parseInt(year, 10)),
    };
  }

  // Check for approximate like "early Jan 2026", "mid-Feb 2026", "late March 2026"
  const approxMatch = trimmed.match(
    /^(early|mid|late)[-\s]*(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*\s*(\d{4})$/i
  );
  if (approxMatch) {
    const prefix = approxMatch[1];
    const monthNum = getMonthNumber(approxMatch[2]);
    const year = approxMatch[3];
    let day = 1;
    if (prefix === "mid") day = 15;
    if (prefix === "late") day = 25;
    const startDate = `${year}-${monthNum.toString().padStart(2, "0")}-${day
      .toString()
      .padStart(2, "0")}`;
    return {
      precision: "approximate",
      startDate,
      dateLabel: `${capitalize(prefix)} ${formatMonthYear(
        monthNum,
        parseInt(year, 10)
      )}`,
    };
  }

  // Check for year only like "2026"
  const yearMatch = trimmed.match(/^(\d{4})$/);
  if (yearMatch) {
    return {
      precision: "year",
      startDate: `${yearMatch[1]}-01-01`,
      dateLabel: yearMatch[1],
    };
  }

  // Check for date range like "Jan-Feb 2026" or "Jan 2026 - Mar 2026"
  const rangeMatch = trimmed.match(
    /^(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*[-–\s]+(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*\s*(\d{4})$/i
  );
  if (rangeMatch) {
    const startMonth = getMonthNumber(rangeMatch[1]);
    const endMonth = getMonthNumber(rangeMatch[2]);
    const year = rangeMatch[3];
    return {
      precision: "duration",
      startDate: `${year}-${startMonth.toString().padStart(2, "0")}-01`,
      endDate: `${year}-${endMonth.toString().padStart(2, "0")}-28`,
      dateLabel: `${getMonthAbbrev(startMonth)}–${getMonthAbbrev(
        endMonth
      )} ${year}`,
    };
  }

  // Try parsing as exact date (YYYY-MM-DD or MM/DD/YYYY)
  const isoMatch = trimmed.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (isoMatch) {
    const startDate = `${isoMatch[1]}-${isoMatch[2].padStart(
      2,
      "0"
    )}-${isoMatch[3].padStart(2, "0")}`;
    return {
      precision: "exact",
      startDate,
      dateLabel: formatExactDate(startDate),
    };
  }

  const usMatch = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (usMatch) {
    const startDate = `${usMatch[3]}-${usMatch[1].padStart(
      2,
      "0"
    )}-${usMatch[2].padStart(2, "0")}`;
    return {
      precision: "exact",
      startDate,
      dateLabel: formatExactDate(startDate),
    };
  }

  // Default: treat as label only
  return {
    precision: "approximate",
    dateLabel: input.trim(),
  };
}

/**
 * Compute a date from relative values
 */
export function computeRelativeDate(
  value: number,
  unit: RelativeDate["unit"],
  direction: RelativeDate["direction"]
): Date {
  const now = new Date();
  const multiplier = direction === "ago" ? -1 : 1;

  switch (unit) {
    case "days":
      now.setDate(now.getDate() + value * multiplier);
      break;
    case "weeks":
      now.setDate(now.getDate() + value * 7 * multiplier);
      break;
    case "months":
      now.setMonth(now.getMonth() + value * multiplier);
      break;
    case "years":
      now.setFullYear(now.getFullYear() + value * multiplier);
      break;
  }

  return now;
}

/**
 * Clinical narrative sort order — the logical sequence a reviewer
 * expects to read when no concrete dates are available.
 * Lower number = earlier in the narrative.
 */
const CLINICAL_NARRATIVE_ORDER: Record<string, number> = {
  SYMPTOM: 10,
  DIAGNOSIS: 20,
  IMAGING: 30, // prior / diagnostic imaging
  LAB: 35,
  CONSULT: 40,
  CONSERVATIVE_THERAPY: 50,
  MEDICATION: 55,
  PROCEDURE: 60,
  ED_ADMISSION: 65,
  FUNCTION_CHANGE: 70,
  ADVERSE_EVENT: 75,
  FOLLOWUP: 80,
  PRIOR_AUTH: 85,
  DENIAL: 90,
  REQUEST: 100, // current request — always last
};

/** Sentinel base so undated events always sort after dated ones */
const UNDATED_BASE = new Date("2099-01-01").getTime();

/**
 * Compute sort key for a timeline event
 * Lower numbers = earlier in timeline
 */
export function computeSortKey(
  event: TimelineEvent,
  anchorDate?: string
): number {
  // If we have an exact or computed start date, use it
  if (event.start_date) {
    return new Date(event.start_date).getTime();
  }

  // For relative dates, compute from today
  if (event.relative_value) {
    const computed = computeRelativeDate(
      event.relative_value.value,
      event.relative_value.unit,
      event.relative_value.direction
    );
    return computed.getTime();
  }

  // For events with only date_label, try to extract some ordering
  if (event.date_label) {
    const parsed = parseDateInput(event.date_label);
    if (parsed.startDate) {
      return new Date(parsed.startDate).getTime();
    }
  }

  // No date info — fall back to clinical narrative order so events
  // display in a logical sequence (Symptom → Diagnosis → Treatment → Request)
  const narrativeSlot = CLINICAL_NARRATIVE_ORDER[event.event_type] ?? 95;
  return UNDATED_BASE + narrativeSlot;
}

/**
 * Sort events chronologically
 */
export function sortTimelineEvents(
  events: TimelineEvent[],
  anchorDate?: string
): TimelineEvent[] {
  return [...events].sort((a, b) => {
    const keyA = a.sort_key ?? computeSortKey(a, anchorDate);
    const keyB = b.sort_key ?? computeSortKey(b, anchorDate);
    return keyA - keyB;
  });
}

/**
 * Validate timeline events and add warnings
 */
export function validateTimelineEvents(
  events: TimelineEvent[]
): TimelineEvent[] {
  const sortedEvents = sortTimelineEvents(events);

  return sortedEvents.map((event, index) => {
    const warnings: string[] = [];

    // Check for missing date
    if (!event.start_date && !event.date_label && !event.relative_value) {
      warnings.push("Missing date - add a date to strengthen the timeline");
    }

    // Check for missing title
    if (!event.title || event.title.trim().length === 0) {
      warnings.push("Missing title");
    }

    // Check for conservative therapy without duration
    if (
      (event.event_type === "CONSERVATIVE_THERAPY" ||
        event.event_type === "MEDICATION") &&
      !event.end_date &&
      !event.date_label?.match(/\d+\s*(day|week|month)/i)
    ) {
      warnings.push(
        "Adding duration (e.g., '6 weeks') strengthens the timeline"
      );
    }

    // Check for therapy without outcome
    if (
      (event.event_type === "CONSERVATIVE_THERAPY" ||
        event.event_type === "MEDICATION") &&
      !event.outcome
    ) {
      warnings.push("Adding treatment outcome improves appeal strength");
    }

    // Check for events before onset (if onset exists)
    const onsetEvent = sortedEvents.find((e) => e.event_type === "SYMPTOM");
    if (onsetEvent && event.id !== onsetEvent.id) {
      const onsetKey = onsetEvent.sort_key ?? computeSortKey(onsetEvent);
      const eventKey = event.sort_key ?? computeSortKey(event);
      if (eventKey < onsetKey && event.event_type !== "DIAGNOSIS") {
        warnings.push(
          "This event appears before symptom onset - please verify dates"
        );
      }
    }

    return {
      ...event,
      sort_key: computeSortKey(event),
      hasValidationWarning: warnings.length > 0,
      validationMessage: warnings.join("; "),
    };
  });
}

/**
 * Format event for display in timeline
 */
export function formatEventDateDisplay(event: TimelineEvent): string {
  if (event.date_label) {
    return event.date_label;
  }

  if (event.start_date && event.end_date) {
    return `${formatExactDate(event.start_date)} – ${formatExactDate(
      event.end_date
    )}`;
  }

  if (event.start_date) {
    if (event.date_precision === "month") {
      const date = new Date(event.start_date);
      return formatMonthYear(date.getMonth() + 1, date.getFullYear());
    }
    return formatExactDate(event.start_date);
  }

  return "Date not specified";
}

/**
 * Calculate duration between two events or dates
 */
export function calculateDuration(startDate: string, endDate: string): string {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const diffMs = end.getTime() - start.getTime();
  const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays < 7) {
    return `${diffDays} day${diffDays !== 1 ? "s" : ""}`;
  }

  const diffWeeks = Math.round(diffDays / 7);
  if (diffWeeks < 8) {
    return `${diffWeeks} week${diffWeeks !== 1 ? "s" : ""}`;
  }

  const diffMonths = Math.round(diffDays / 30);
  return `${diffMonths} month${diffMonths !== 1 ? "s" : ""}`;
}

// Helper functions
function getMonthNumber(monthStr: string): number {
  const months: Record<string, number> = {
    jan: 1,
    feb: 2,
    mar: 3,
    apr: 4,
    may: 5,
    jun: 6,
    jul: 7,
    aug: 8,
    sep: 9,
    oct: 10,
    nov: 11,
    dec: 12,
  };
  return months[monthStr.toLowerCase().substring(0, 3)] || 1;
}

function getMonthAbbrev(month: number): string {
  const abbrevs = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  return abbrevs[month - 1] || "Jan";
}

function formatMonthYear(month: number, year: number): string {
  return `${getMonthAbbrev(month)} ${year}`;
}

function formatExactDate(isoDate: string): string {
  const date = new Date(isoDate);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function capitalize(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

/**
 * Create a new empty event with defaults
 */
export function createEmptyEvent(
  eventType?: TimelineEvent["event_type"]
): TimelineEvent {
  return {
    id: generateEventId(),
    event_type: eventType || "SYMPTOM",
    date_precision: "exact",
    title: "",
    details: "",
    outcome: null,
    evidence_flags: [],
    function_tags: [],
    isNew: true,
  };
}

/**
 * Create events from suggested templates
 */
export function createEventsFromSuggestions(
  suggestions: {
    event_type: TimelineEvent["event_type"];
    title: string;
    placeholder?: string;
  }[]
): TimelineEvent[] {
  return suggestions.map((suggestion) => ({
    ...createEmptyEvent(suggestion.event_type),
    title: suggestion.title,
    details: "",
  }));
}